/**
 * currentSeason (#2601-A / #2610) — THE season slicing rule for Ernteprognose.
 *
 * ``crop_year`` is the HARVEST SEASON: the calendar year the crop is harvested
 * in. That is what the data producers (wiberry / myfarm backfills) write and
 * what Deckungsbeitrag labels its periods with. Ernteprognose used to slice its
 * Ist by the wall-clock Wirtschaftsjahr window instead, which is a DIFFERENT
 * notion — and for any crop whose harvest ends before ``wj_start_month`` the two
 * are permanently one year apart, so that crop's season was reachable at no
 * wall-clock date at all (#2610: WF Spargel, Mar–Jun; #2601: Erdbeeren, bisected
 * in April). This module is the one place that decides which season is
 * "current"; everything downstream (Ist, presence facet, forecast/plan
 * predicate, banner, chips, overlays) slices off ITS answer, so the surfaces can
 * never disagree (the #2413 invariant).
 *
 * Pure + unit-tested (no React/recharts/network).
 */
import { cropYearNumOf } from './lensGroups';
import type { OutputWindow } from './outputSeries';
import type { OutputCycleInfo } from '../../../hooks/useErnteprognose';

/**
 * 'YYYY-MM' guard for axis candidates. Raw twin ``operation.date`` /
 * ``forecast_date`` strings reach the axis now that no series is date-clamped,
 * and the hook's ``strOrNull`` accepts ANY non-empty string — an unparseable one
 * would sort above every real month, become the window end, and throw inside
 * ``lastDayOfMonth`` (``new Date(NaN).toISOString()``): a white page.
 *
 * The MONTH is range-checked, not merely two digits: a corrupt ``2027-00`` or
 * ``2027-13`` passes the ``currentSeason ± 1`` clamp, and if it sorts below the
 * centred start it becomes ``startYm`` — where ``wjBuckets``' day/week branch
 * (day is the DEFAULT grain) calls ``firstDayOfMonth('2027-00')`` and throws
 * ``RangeError: Invalid time value`` on the resulting Invalid Date. Only the
 * start side crashes; ``lastDayOfMonth`` normalises via ``Date.UTC``.
 */
const YM_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;

/**
 * The current harvest season as a ``crop_year``.
 *
 * Base case: the wall-clock Wirtschaftsjahr start year (``baseWj``, from
 * ``currentWirtschaftsjahr``). PROMOTED to ``todayYear`` when BOTH hold:
 *
 *   1. **We are pre-rollover** — ``baseWj < todayYear``, i.e. the WJ still runs
 *      under last year's label because today's month precedes ``wj_start_month``.
 *      This is the ONLY window in which a season can legitimately lead the WJ,
 *      and it is where #2610 bites (2026-05-15, July start: the WJ reads 2025
 *      while Spargel Saison 2026 is mid-harvest). Outside it — after the
 *      rollover, or on a January-start farm where season and WJ always coincide
 *      — no promotion is possible at all.
 *   2. **That season is demonstrably harvesting** — some ``crop_year ===
 *      todayYear`` cycle has recorded picks. Picks are the evidence, so a
 *      genuine planning register row (a future ``crop_year`` with no harvest)
 *      keeps its "Planung" classification.
 *
 * Because ``baseWj ∈ {todayYear − 1, todayYear}``, condition 1 alone bounds the
 * promotion to exactly one year AND pins the promoted season to the current
 * calendar year — a harvest season is named by the calendar year it is
 * harvested in, so no stray far-future ``crop_year`` can run the page forward.
 *
 * **Deliberately does NOT fail open.** ``pickCycles`` is empty while the
 * presence aggregate is loading or after it errors, so both degrade to
 * ``baseWj`` — the pre-#2610 wall-clock behaviour. That is the conservative
 * direction: the alternative (promote on mere MEMBERSHIP of a ``todayYear``
 * cycle) would promote into a season that has not started, turning every
 * January register row into a false "current season" with an empty Ist. The
 * page's other consumers of this aggregate fail OPEN on error (``visibleGroups``
 * / ``refinementPresence`` show everything) because over-showing is safe there;
 * here over-promoting is not.
 */
export function resolveCurrentSeason(
  cycles: OutputCycleInfo[],
  pickCycles: ReadonlySet<string>,
  baseWj: number,
  todayYear: number,
): number {
  if (baseWj >= todayYear) return baseWj;
  const nextIsHarvesting = cycles.some(
    (c) => cropYearNumOf(c) === todayYear && pickCycles.has(c.cropCycleId),
  );
  return nextIsHarvesting ? todayYear : baseWj;
}

/**
 * Season label (#2610 product decision): "Saison 2026", following the
 * Deckungsbeitrag precedent of labelling periods by ``crop_year``. Deliberately
 * NOT "WJ 26/27" — a berry season is a season, not a fiscal year.
 */
export function saisonLabel(season: number): string {
  return `Saison ${season}`;
}

/**
 * The chart AXIS window for a season (#2601/#2610): ``centered`` (the #2436
 * Heute-centred ±1-year span) WIDENED to contain every date the chart will draw.
 *
 * Why it must widen: ``buildOutputChart`` emits only the buckets of
 * ``wjBuckets(window, grain)`` and clips EVERY series to them, while the Schläge
 * table has no window at all. Anything drawn but outside the spine therefore
 * disappears from the chart — and from the banner verdict, which grades the chart
 * rows — while the table still shows it. That is the #2413 disagreement the
 * season seam exists to close, and it became reachable the moment the series
 * stopped being date-clamped: between January and ``wj_start_month`` the current
 * season is the PREVIOUS calendar year, so its picks, an early crop's spring
 * ``forecast_date``, and the prior-year curves (which ``shiftYears`` lands in
 * calendar year ``currentSeason`` — or ``currentSeason + 1`` for a season that
 * crosses the calendar boundary) can all sit more than 12 months from Heute.
 *
 * Why it is BOUNDED and VALIDATED: candidates are raw twin date strings now. A
 * candidate counts only if it parses as ``YYYY-MM`` and falls within
 * ``currentSeason ± 1`` — the only calendar years this chart can legitimately
 * draw. Without that a one-digit year typo (2016 for 2026) would stretch the day
 * spine to thousands of buckets, and a non-date string would reach
 * ``lastDayOfMonth`` and throw.
 *
 * ``centered`` is always the MINIMUM: this only ever widens, never narrows, so
 * whenever the season already sits inside it the axis is byte-identical to
 * #2436's and the default view is unchanged. When it does widen, the chart's zoom
 * viewport (which defaults to the full spine) opens wider and "Heute" stops being
 * centred — the deliberate trade: showing the season the farmer came for beats
 * centring Heute over an empty range.
 */
export function seasonAxisWindow(
  centered: OutputWindow,
  currentSeason: number,
  candidateDates: Iterable<string | null | undefined>,
): OutputWindow {
  let { startYm, endYm } = centered;
  const floorYm = `${currentSeason - 1}-01`;
  const ceilYm = `${currentSeason + 1}-12`;
  for (const raw of candidateDates) {
    if (typeof raw !== 'string') continue;
    const ym = raw.slice(0, 7);
    if (!YM_PATTERN.test(ym) || ym < floorYm || ym > ceilYm) continue;
    if (ym < startYm) startYm = ym;
    if (ym > endYm) endYm = ym;
  }
  return { startYm, endYm };
}
